import React, { useState, useEffect, useRef } from 'react';
import { Link, useOutletContext } from 'react-router-dom';
import { ArrowLeft, Printer, Calendar, BarChart3, Smartphone, Mail, Coins, File, TrendingUp, Building, ArrowDownRight, Trophy, Users, ClipboardList, TrendingDown, Wrench } from 'lucide-react';
import api from '../services/api';
import useWorkspaceBasePath from '../hooks/useWorkspaceBasePath';

const BusinessReport = ({ projectMode = false }) => {
  // Contexte du projet si en mode projet
  const outletContext = useOutletContext();
  const projectId = projectMode ? outletContext?.projectId : null;
  const routeBase = useWorkspaceBasePath();
  
  // Base URL de l'API selon le mode
  const apiBase = projectMode && projectId ? `/projects/${projectId}/workspace` : '/business';
  
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [settings, setSettings] = useState(null);
  const printRef = useRef();
  
  // Périodes prédéfinies
  const [periodeType, setPeriodeType] = useState('mois_courant');
  const [dateDebut, setDateDebut] = useState('');
  const [dateFin, setDateFin] = useState('');

  useEffect(() => {
    // Définir les dates selon la période sélectionnée
    const today = new Date();
    let debut, fin;
    
    switch (periodeType) {
      case 'mois_courant':
        debut = new Date(today.getFullYear(), today.getMonth(), 1);
        fin = today;
        break;
      case 'mois_precedent':
        debut = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        fin = new Date(today.getFullYear(), today.getMonth(), 0);
        break;
      case 'trimestre':
        const quarterStart = Math.floor(today.getMonth() / 3) * 3;
        debut = new Date(today.getFullYear(), quarterStart, 1);
        fin = today;
        break;
      case 'semestre':
        const semestreStart = today.getMonth() < 6 ? 0 : 6;
        debut = new Date(today.getFullYear(), semestreStart, 1);
        fin = today;
        break;
      case 'annee':
        debut = new Date(today.getFullYear(), 0, 1);
        fin = today;
        break;
      case 'personnalise':
        return; // Ne pas modifier les dates
      default:
        debut = new Date(today.getFullYear(), today.getMonth(), 1);
        fin = today;
    }
    
    setDateDebut(debut.toISOString().split('T')[0]);
    setDateFin(fin.toISOString().split('T')[0]);
  }, [periodeType]);

  const loadReport = async () => {
    if (!dateDebut || !dateFin) return;
    
    setLoading(true);
    try {
      const res = await api.get(`${apiBase}/report?date_debut=${dateDebut}&date_fin=${dateFin}`);
      setData(res.data.report);
      setSettings(res.data.entreprise);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {
    const printContent = printRef.current;
    const printWindow = window.open('', '_blank');
    
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>Bilan - ${settings?.nom_entreprise || 'Mon Entreprise'}</title>
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body { 
            font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; 
            padding: 20px;
            color: #333;
            font-size: 12px;
          }
          .header { 
            display: flex; 
            justify-content: space-between; 
            align-items: flex-start;
            border-bottom: 3px solid #4F46E5;
            padding-bottom: 20px;
            margin-bottom: 20px;
          }
          .company-info h1 { 
            color: #4F46E5; 
            font-size: 24px; 
            margin-bottom: 8px; 
          }
          .company-info p { 
            color: #666; 
            font-size: 11px;
            line-height: 1.6;
          }
          .report-title {
            text-align: right;
          }
          .report-title h2 {
            font-size: 18px;
            color: #333;
            margin-bottom: 5px;
          }
          .report-title .period {
            color: #4F46E5;
            font-weight: 600;
          }
          .section { 
            margin-bottom: 25px; 
            page-break-inside: avoid;
          }
          .section-title { 
            font-size: 14px; 
            font-weight: bold; 
            color: #4F46E5;
            border-bottom: 2px solid #E5E7EB;
            padding-bottom: 8px;
            margin-bottom: 15px;
          }
          .grid-2 { 
            display: grid; 
            grid-template-columns: 1fr 1fr; 
            gap: 15px; 
          }
          .grid-3 { 
            display: grid; 
            grid-template-columns: 1fr 1fr 1fr; 
            gap: 15px; 
          }
          .stat-box { 
            background: #F9FAFB; 
            border: 1px solid #E5E7EB;
            border-radius: 8px;
            padding: 12px; 
          }
          .stat-box .label { 
            font-size: 10px; 
            color: #6B7280; 
            text-transform: uppercase;
            letter-spacing: 0.5px;
          }
          .stat-box .value { 
            font-size: 18px; 
            font-weight: bold; 
            color: #111827;
            margin-top: 4px;
          }
          .stat-box .value.positive { color: #059669; }
          .stat-box .value.negative { color: #DC2626; }
          .stat-box .sub { 
            font-size: 10px; 
            color: #9CA3AF;
            margin-top: 2px;
          }
          table { 
            width: 100%; 
            border-collapse: collapse; 
            font-size: 11px;
          }
          th, td { 
            padding: 8px 10px; 
            text-align: left; 
            border-bottom: 1px solid #E5E7EB; 
          }
          th { 
            background: #F3F4F6; 
            font-weight: 600;
            color: #374151;
          }
          tr:last-child td { border-bottom: none; }
          .text-right { text-align: right; }
          .footer {
            margin-top: 30px;
            padding-top: 15px;
            border-top: 1px solid #E5E7EB;
            text-align: center;
            color: #9CA3AF;
            font-size: 10px;
          }
          .summary-row {
            display: flex;
            justify-content: space-between;
            padding: 10px 0;
            border-bottom: 1px dashed #E5E7EB;
          }
          .summary-row:last-child { border-bottom: none; }
          .summary-row .label { color: #6B7280; }
          .summary-row .value { font-weight: 600; }
          .highlight-box {
            background: linear-gradient(135deg, #4F46E5 0%, #7C3AED 100%);
            color: white;
            border-radius: 8px;
            padding: 15px;
            text-align: center;
          }
          .highlight-box .label { 
            font-size: 11px; 
            opacity: 0.9;
          }
          .highlight-box .value { 
            font-size: 24px; 
            font-weight: bold;
            margin-top: 5px;
          }
          @media print {
            body { padding: 0; }
            .section { page-break-inside: avoid; }
          }
        </style>
      </head>
      <body>
        ${printContent.innerHTML}
      </body>
      </html>
    `);
    
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 250);
  };

  const formatNumber = (num) => {
    return new Intl.NumberFormat('fr-FR').format(num || 0);
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const getPeriodLabel = () => {
    const labels = {
      'mois_courant': 'Mois en cours',
      'mois_precedent': 'Mois précédent',
      'trimestre': 'Trimestre en cours',
      'semestre': 'Semestre en cours',
      'annee': 'Année en cours',
      'personnalise': 'Période personnalisée'
    };
    return labels[periodeType] || 'Période';
  };

  return (
    <div className="p-6 max-w-6xl mx-auto">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div className="flex items-center gap-3">
          <Link to={routeBase} className="p-2 bg-gray-100 rounded-lg hover:bg-gray-200">
            <ArrowLeft className="w-4 h-4" />
          </Link>
          <div>
            <h1 className="text-xl md:text-2xl font-bold text-gray-800">Bilan Périodique</h1>
            <p className="text-gray-500 text-xs md:text-sm">Générez un rapport détaillé de votre activité</p>
          </div>
        </div>
        {data && (
          <button
            onClick={handlePrint}
            className="flex items-center justify-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700"
          >
            <Printer className="w-4 h-4" />
            Imprimer
          </button>
        )}
      </div>

      {/* Sélection de période */}
      <div className="bg-white rounded-xl shadow p-4 md:p-6 mb-6">
        <h2 className="font-semibold text-gray-800 mb-4 flex items-center gap-2"><Calendar className="w-4 h-4" /> Sélectionner la période</h2>
        
        <div className="grid grid-cols-3 md:grid-cols-6 gap-2 mb-4">
          {[
            { value: 'mois_courant', label: 'Ce mois' },
            { value: 'mois_precedent', label: 'Mois préc.' },
            { value: 'trimestre', label: 'Trimestre' },
            { value: 'semestre', label: 'Semestre' },
            { value: 'annee', label: 'Année' },
            { value: 'personnalise', label: 'Personnalisé' }
          ].map(p => (
            <button
              key={p.value}
              onClick={() => setPeriodeType(p.value)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition ${
                periodeType === p.value
                  ? 'bg-indigo-600 text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {p.label}
            </button>
          ))}
        </div>
        
        <div className="flex flex-col sm:flex-row sm:flex-wrap items-stretch sm:items-end gap-3 md:gap-4">
          <div className="flex-1 min-w-[140px]">
            <label className="block text-sm text-gray-600 mb-1">Date de début</label>
            <input
              type="date"
              value={dateDebut}
              onChange={(e) => {
                setDateDebut(e.target.value);
                setPeriodeType('personnalise');
              }}
              className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500"
            />
          </div>
          <div className="flex-1 min-w-[140px]">
            <label className="block text-sm text-gray-600 mb-1">Date de fin</label>
            <input
              type="date"
              value={dateFin}
              onChange={(e) => {
                setDateFin(e.target.value);
                setPeriodeType('personnalise');
              }}
              className="border rounded-lg px-4 py-2 focus:ring-2 focus:ring-indigo-500"
            />
          </div>
          <button
            onClick={loadReport}
            disabled={loading || !dateDebut || !dateFin}
            className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {loading ? (
              <>
                <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full"></div>
                Génération...
              </>
            ) : (
              <>
                <BarChart3 className="w-4 h-4" />
                Générer le bilan
              </>
            )}
          </button>
        </div>
      </div>

      {/* Contenu du bilan */}
      {data && (
        <div ref={printRef}>
          {/* En-tête entreprise */}
          <div className="header bg-white rounded-xl shadow p-6 mb-6" style={{ display: 'flex', justifyContent: 'space-between' }}>
            <div className="company-info">
              <h1 style={{ fontSize: '24px', color: '#4F46E5', fontWeight: 'bold' }}>
                {settings?.nom_entreprise || 'Mon Entreprise'}
              </h1>
              {settings?.adresse && <p>{settings.adresse}</p>}
              {settings?.telephone && <p><Smartphone className="w-4 h-4 inline" /> {settings.telephone}</p>}
              {settings?.email && <p><Mail className="w-4 h-4 inline" /> {settings.email}</p>}
              {settings?.numero_registre && <p>RCCM: {settings.numero_registre}</p>}
              {settings?.numero_fiscal && <p>NIF: {settings.numero_fiscal}</p>}
            </div>
            <div className="report-title" style={{ textAlign: 'right' }}>
              <h2 style={{ fontSize: '18px', fontWeight: 'bold' }}>BILAN D'ACTIVITÉ</h2>
              <p className="period" style={{ color: '#4F46E5', fontWeight: '600' }}>
                {getPeriodLabel()}
              </p>
              <p style={{ color: '#666', marginTop: '5px' }}>
                Du {formatDate(dateDebut)} au {formatDate(dateFin)}
              </p>
            </div>
          </div>

          {/* ═══════════════ RÉSUMÉ PRINCIPAL ═══════════════ */}
          <div className="bg-white rounded-xl shadow p-6 mb-6">
            <h3 className="font-semibold text-indigo-600 border-b-2 border-indigo-100 pb-2 mb-5 text-base flex items-center gap-2"><BarChart3 className="w-4 h-4" /> Résumé financier</h3>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <div className="bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl p-5 text-white">
                <p className="text-xs opacity-80 uppercase tracking-wide">Chiffre d'affaires net</p>
                <p className="text-2xl font-bold mt-1">{formatNumber(data.resume?.chiffre_affaires)} XOF</p>
                <p className="text-xs opacity-70 mt-1">Ventes - Avoirs - Dépôts crédit</p>
              </div>
              <div className="bg-orange-50 border border-orange-200 rounded-xl p-5">
                <p className="text-xs text-gray-500 uppercase tracking-wide">Coût des ventes</p>
                <p className="text-2xl font-bold mt-1 text-orange-600">{formatNumber(data.resume?.cout_ventes)} XOF</p>
                <p className="text-xs text-gray-400 mt-1">Prix d'achat des produits vendus</p>
              </div>
              <div className={`${(data.resume?.marge_brute || 0) >= 0 ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'} border rounded-xl p-5`}>
                <p className="text-xs text-gray-500 uppercase tracking-wide">Marge brute</p>
                <p className={`text-2xl font-bold mt-1 ${(data.resume?.marge_brute || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {formatNumber(data.resume?.marge_brute)} XOF
                </p>
                <p className="text-xs text-gray-400 mt-1">Taux de marge : {data.resume?.taux_marge || 0}%</p>
              </div>
              <div className="bg-red-50 border border-red-200 rounded-xl p-5">
                <p className="text-xs text-gray-500 uppercase tracking-wide">Total sorties</p>
                <p className="text-2xl font-bold mt-1 text-red-600">{formatNumber(data.resume?.depenses_totales)} XOF</p>
                <p className="text-xs text-gray-400 mt-1">Dépenses + Décaissements</p>
              </div>
              <div className={`${(data.resume?.benefice_net || 0) >= 0 ? 'bg-emerald-50 border-emerald-200' : 'bg-red-50 border-red-200'} border rounded-xl p-5`}>
                <p className="text-xs text-gray-500 uppercase tracking-wide">Bénéfice net</p>
                <p className={`text-2xl font-bold mt-1 ${(data.resume?.benefice_net || 0) >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                  {formatNumber(data.resume?.benefice_net)} XOF
                </p>
                <p className="text-xs text-gray-400 mt-1">Marge brute - Sorties</p>
              </div>
              <div className={`${(data.resume?.tresorerie || 0) >= 0 ? 'bg-blue-50 border-blue-200' : 'bg-red-50 border-red-200'} border rounded-xl p-5`}>
                <p className="text-xs text-gray-500 uppercase tracking-wide">Trésorerie (Caisse)</p>
                <p className={`text-2xl font-bold mt-1 ${(data.resume?.tresorerie || 0) >= 0 ? 'text-blue-600' : 'text-red-600'}`}>
                  {formatNumber(data.resume?.tresorerie)} XOF
                </p>
                <p className="text-xs text-gray-400 mt-1">Flux réel de caisse</p>
              </div>
            </div>
          </div>

          {/* ═══════════════ DÉTAIL DU CHIFFRE D'AFFAIRES ═══════════════ */}
          <div className="section bg-white rounded-xl shadow p-6 mb-6">
            <h3 className="font-semibold text-indigo-600 border-b-2 border-indigo-100 pb-2 mb-5 text-base flex items-center gap-2"><Coins className="w-4 h-4" /> Chiffre d'affaires — Détail</h3>
            
            {/* Décomposition ligne par ligne */}
            <div className="space-y-2 mb-5">
              <div className="flex justify-between items-center py-2 px-3 bg-green-50 rounded-lg">
                <span className="text-sm text-gray-700">Encaissements reçus</span>
                <span className="font-semibold text-green-600">+{formatNumber(data.chiffre_affaires?.encaissements)} XOF</span>
              </div>
              {(data.chiffre_affaires?.service_requests || 0) > 0 && (
                <div className="flex justify-between items-center py-2 px-3 bg-purple-50 rounded-lg">
                  <span className="text-sm text-gray-700">Revenus services / commandes</span>
                  <span className="font-semibold text-purple-600">+{formatNumber(data.chiffre_affaires?.service_requests)} XOF</span>
                </div>
              )}
              {(data.chiffre_affaires?.depots_credit || 0) > 0 && (
                <div className="flex justify-between items-center py-2 px-3 bg-yellow-50 rounded-lg">
                  <span className="text-sm text-gray-700">− Dépôts crédit client <span className="text-xs text-gray-400">(non-revenu)</span></span>
                  <span className="font-semibold text-yellow-700">−{formatNumber(data.chiffre_affaires?.depots_credit)} XOF</span>
                </div>
              )}
              {(data.chiffre_affaires?.avoirs || 0) > 0 && (
                <div className="flex justify-between items-center py-2 px-3 bg-orange-50 rounded-lg">
                  <span className="text-sm text-gray-700">− Avoirs / Retours</span>
                  <span className="font-semibold text-orange-600">−{formatNumber(data.chiffre_affaires?.avoirs)} XOF</span>
                </div>
              )}
              <div className="flex justify-between items-center py-3 px-3 bg-gradient-to-r from-indigo-100 to-purple-100 rounded-lg border-t-2 border-indigo-300">
                <span className="font-bold text-gray-800">= Chiffre d'affaires net</span>
                <span className="text-xl font-bold text-indigo-700">{formatNumber(data.resume?.chiffre_affaires)} XOF</span>
              </div>
            </div>
          </div>

          {/* ═══════════════ FACTURATION ═══════════════ */}
          <div className="section bg-white rounded-xl shadow p-6 mb-6">
            <h3 className="font-semibold text-indigo-600 border-b-2 border-indigo-100 pb-2 mb-5 text-base flex items-center gap-2"><File className="w-4 h-4" /> Facturation</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <p className="text-xs text-gray-500 uppercase">Total facturé</p>
                <p className="text-xl font-bold mt-1">{formatNumber(data.chiffre_affaires?.total_factures)} XOF</p>
                <p className="text-xs text-gray-400 mt-1">{data.chiffre_affaires?.total_factures_count || 0} facture(s)</p>
              </div>
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <p className="text-xs text-gray-500 uppercase">Payées</p>
                <p className="text-xl font-bold mt-1 text-green-600">{formatNumber(data.chiffre_affaires?.factures_payees)} XOF</p>
                <p className="text-xs text-gray-400 mt-1">{data.chiffre_affaires?.factures_payees_count || 0} facture(s)</p>
              </div>
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                <p className="text-xs text-gray-500 uppercase">En attente de paiement</p>
                <p className="text-xl font-bold mt-1 text-orange-600">{formatNumber(data.chiffre_affaires?.factures_en_attente)} XOF</p>
                <p className="text-xs text-gray-400 mt-1">{data.chiffre_affaires?.factures_en_attente_count || 0} facture(s)</p>
              </div>
            </div>
          </div>

          {/* ═══════════════ RENTABILITÉ ═══════════════ */}
          <div className="section bg-white rounded-xl shadow p-6 mb-6">
            <h3 className="font-semibold text-indigo-600 border-b-2 border-indigo-100 pb-2 mb-5 text-base flex items-center gap-2"><TrendingUp className="w-4 h-4" /> Rentabilité</h3>
            <div className="space-y-2">
              <div className="flex justify-between items-center py-2 px-3 bg-indigo-50 rounded-lg">
                <span className="text-sm text-gray-700">Chiffre d'affaires net</span>
                <span className="font-semibold text-indigo-600">{formatNumber(data.resume?.chiffre_affaires)} XOF</span>
              </div>
              <div className="flex justify-between items-center py-2 px-3 bg-orange-50 rounded-lg">
                <span className="text-sm text-gray-700">− Coût des ventes</span>
                <span className="font-semibold text-orange-600">−{formatNumber(data.resume?.cout_ventes)} XOF</span>
              </div>
              <div className="flex justify-between items-center py-2 px-3 bg-green-50 rounded-lg border-t border-green-300">
                <span className="text-sm font-medium text-gray-700">= Marge brute <span className="text-xs text-gray-400">({data.resume?.taux_marge || 0}%)</span></span>
                <span className={`font-bold ${(data.resume?.marge_brute || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>{formatNumber(data.resume?.marge_brute)} XOF</span>
              </div>
              <div className="flex justify-between items-center py-2 px-3 bg-red-50 rounded-lg">
                <span className="text-sm text-gray-700">− Sorties (Dépenses + Décaissements)</span>
                <span className="font-semibold text-red-600">−{formatNumber(data.resume?.depenses_totales)} XOF</span>
              </div>
              <div className={`flex justify-between items-center py-3 px-3 rounded-lg border-t-2 ${(data.resume?.benefice_net || 0) >= 0 ? 'bg-emerald-100 border-emerald-400' : 'bg-red-100 border-red-400'}`}>
                <span className="font-bold text-gray-800">= Bénéfice net</span>
                <span className={`text-xl font-bold ${(data.resume?.benefice_net || 0) >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>{formatNumber(data.resume?.benefice_net)} XOF</span>
              </div>
            </div>
          </div>

          {/* ═══════════════ TRÉSORERIE ═══════════════ */}
          <div className="section bg-white rounded-xl shadow p-6 mb-6">
            <h3 className="font-semibold text-indigo-600 border-b-2 border-indigo-100 pb-2 mb-5 text-base flex items-center gap-2"><Building className="w-4 h-4" /> Trésorerie (Flux de caisse)</h3>
            <div className="space-y-2 mb-5">
              <div className="flex justify-between items-center py-2 px-3 bg-green-50 rounded-lg">
                <span className="text-sm text-gray-700">Encaissements reçus</span>
                <span className="font-semibold text-green-600">+{formatNumber(data.tresorerie?.encaissements)} XOF</span>
              </div>
              {(data.tresorerie?.encaissements_credit || 0) > 0 && (
                <div className="flex justify-between items-center py-2 px-3 bg-yellow-50 rounded-lg">
                  <span className="text-sm text-gray-700">− Paiements par crédit client <span className="text-xs text-gray-400">(pas de cash réel)</span></span>
                  <span className="font-semibold text-yellow-700">−{formatNumber(data.tresorerie?.encaissements_credit)} XOF</span>
                </div>
              )}
              {(data.chiffre_affaires?.service_requests || 0) > 0 && (
                <div className="flex justify-between items-center py-2 px-3 bg-purple-50 rounded-lg">
                  <span className="text-sm text-gray-700">Revenus services</span>
                  <span className="font-semibold text-purple-600">+{formatNumber(data.chiffre_affaires?.service_requests)} XOF</span>
                </div>
              )}
              <div className="flex justify-between items-center py-2 px-3 bg-red-50 rounded-lg">
                <span className="text-sm text-gray-700">− Décaissements</span>
                <span className="font-semibold text-red-600">−{formatNumber(data.tresorerie?.decaissements)} XOF</span>
              </div>
              {(data.tresorerie?.achats_stock || 0) > 0 && (
                <div className="flex justify-between items-center py-2 px-3 bg-amber-50 rounded-lg">
                  <span className="text-sm text-gray-700">− Achats de stock</span>
                  <span className="font-semibold text-amber-600">−{formatNumber(data.tresorerie?.achats_stock)} XOF</span>
                </div>
              )}
              <div className="flex justify-between items-center py-2 px-3 bg-red-50 rounded-lg">
                <span className="text-sm text-gray-700">− Dépenses</span>
                <span className="font-semibold text-red-500">−{formatNumber(data.tresorerie?.depenses)} XOF</span>
              </div>
              <div className={`flex justify-between items-center py-3 px-3 rounded-lg border-t-2 ${(data.tresorerie?.solde || 0) >= 0 ? 'bg-blue-100 border-blue-400' : 'bg-red-100 border-red-400'}`}>
                <span className="font-bold text-gray-800">= Solde de caisse</span>
                <span className={`text-xl font-bold ${(data.tresorerie?.solde || 0) >= 0 ? 'text-blue-700' : 'text-red-700'}`}>{formatNumber(data.tresorerie?.solde)} XOF</span>
              </div>
            </div>
            
            {data.tresorerie?.paiements_par_mode?.length > 0 && (
              <div>
                <p className="text-sm font-medium text-gray-700 mb-2">Encaissements par mode de paiement</p>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="text-left p-2 rounded-tl-lg">Mode</th>
                      <th className="text-right p-2">Nombre</th>
                      <th className="text-right p-2 rounded-tr-lg">Montant</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.tresorerie.paiements_par_mode.map((mode, idx) => (
                      <tr key={idx} className="border-b border-gray-100">
                        <td className="p-2 capitalize">{mode.methode || 'Non spécifié'}</td>
                        <td className="p-2 text-right">{mode.count}</td>
                        <td className="p-2 text-right font-medium">{formatNumber(mode.total)} XOF</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* ═══════════════ DÉPENSES ET SORTIES ═══════════════ */}
          {(data.depenses?.par_categorie?.length > 0 || data.depenses?.total > 0) && (
            <div className="section bg-white rounded-xl shadow p-6 mb-6">
              <h3 className="font-semibold text-indigo-600 border-b-2 border-indigo-100 pb-2 mb-5 text-base flex items-center gap-2"><ArrowDownRight className="w-4 h-4" /> Dépenses et Sorties</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-5">
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <p className="text-xs text-gray-500 uppercase">Dépenses</p>
                  <p className="text-xl font-bold text-red-600">{formatNumber(data.depenses?.total)} XOF</p>
                  <p className="text-xs text-gray-400 mt-1">{data.depenses?.count || 0} dépense(s)</p>
                </div>
                <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                  <p className="text-xs text-gray-500 uppercase">Décaissements</p>
                  <p className="text-xl font-bold text-orange-600">{formatNumber(data.depenses?.decaissements)} XOF</p>
                  <p className="text-xs text-gray-400 mt-1">Paiements sortants</p>
                </div>
                {(data.depenses?.achats_stock || 0) > 0 && (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                    <p className="text-xs text-gray-500 uppercase">Achats de stock</p>
                    <p className="text-xl font-bold text-amber-600">{formatNumber(data.depenses?.achats_stock)} XOF</p>
                    <p className="text-xs text-gray-400 mt-1">Investissement stock</p>
                  </div>
                )}
                <div className="bg-gray-100 border border-gray-300 rounded-lg p-4">
                  <p className="text-xs text-gray-500 uppercase">Total sorties (hors stock)</p>
                  <p className="text-xl font-bold text-gray-800">{formatNumber(data.depenses?.total_sorties)} XOF</p>
                  <p className="text-xs text-gray-400 mt-1">Impacte le bénéfice</p>
                </div>
              </div>
              
              {data.depenses?.par_categorie?.length > 0 && (
                <>
                  <p className="text-sm font-medium text-gray-700 mb-2">Dépenses par catégorie</p>
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-gray-50">
                        <th className="text-left p-2">Catégorie</th>
                        <th className="text-right p-2">Nombre</th>
                        <th className="text-right p-2">Montant</th>
                        <th className="text-right p-2">%</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.depenses.par_categorie.map((cat, idx) => (
                        <tr key={idx} className="border-b border-gray-100">
                          <td className="p-2 capitalize">{cat.categorie || 'Non catégorisé'}</td>
                          <td className="p-2 text-right">{cat.count}</td>
                          <td className="p-2 text-right font-medium">{formatNumber(cat.total)} XOF</td>
                          <td className="p-2 text-right text-gray-500">
                            {data.depenses.total > 0 ? ((cat.total / data.depenses.total) * 100).toFixed(1) : 0}%
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="bg-gray-100 font-bold">
                        <td className="p-2">Total dépenses</td>
                        <td className="p-2 text-right">{data.depenses.count}</td>
                        <td className="p-2 text-right">{formatNumber(data.depenses.total)} XOF</td>
                        <td className="p-2 text-right">100%</td>
                      </tr>
                    </tfoot>
                  </table>
                </>
              )}
            </div>
          )}

          {/* ═══════════════ TOP PRODUITS & CLIENTS ═══════════════ */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            {/* Top Produits */}
            {data.produits?.top_vendus?.length > 0 && (
              <div className="section bg-white rounded-xl shadow p-6">
                <h3 className="font-semibold text-indigo-600 border-b-2 border-indigo-100 pb-2 mb-4 text-base flex items-center gap-2"><Trophy className="w-4 h-4" /> Top Produits vendus</h3>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="text-left p-2 rounded-tl-lg">Produit</th>
                      <th className="text-right p-2">Qté</th>
                      <th className="text-right p-2 rounded-tr-lg">CA généré</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.produits.top_vendus.map((prod, idx) => (
                      <tr key={idx} className="border-b border-gray-100">
                        <td className="p-2">
                          <span className="font-medium">{prod.nom || 'Produit supprimé'}</span>
                          {prod.code_produit && <span className="text-xs text-gray-400 ml-2">#{prod.code_produit}</span>}
                        </td>
                        <td className="p-2 text-right">{prod.quantite_vendue}</td>
                        <td className="p-2 text-right font-medium">{formatNumber(prod.total_ventes)} XOF</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Top Clients */}
            {data.clients?.top_clients?.length > 0 && (
              <div className="section bg-white rounded-xl shadow p-6">
                <h3 className="font-semibold text-indigo-600 border-b-2 border-indigo-100 pb-2 mb-4 text-base flex items-center gap-2"><Users className="w-4 h-4" /> Top Clients</h3>
                <div className="mb-3 text-sm text-gray-500 bg-gray-50 rounded-lg px-3 py-2">
                  +{data.clients.nouveaux} nouveau(x) client(s) sur la période
                </div>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="text-left p-2 rounded-tl-lg">Client</th>
                      <th className="text-right p-2">Factures</th>
                      <th className="text-right p-2 rounded-tr-lg">Total achats</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.clients.top_clients.map((client, idx) => (
                      <tr key={idx} className="border-b border-gray-100">
                        <td className="p-2">
                          <span className="font-medium">{client.nom || 'Client anonyme'}</span>
                        </td>
                        <td className="p-2 text-right">{client.nb_factures}</td>
                        <td className="p-2 text-right font-medium">{formatNumber(client.total_achats)} XOF</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Devis et Dettes */}
          {/* ═══════════════ DEVIS & DETTES ═══════════════ */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            {/* Devis */}
            {data.devis?.length > 0 && (
              <div className="section bg-white rounded-xl shadow p-6">
                <h3 className="font-semibold text-indigo-600 border-b-2 border-indigo-100 pb-2 mb-4 text-base flex items-center gap-2"><ClipboardList className="w-4 h-4" /> Devis</h3>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="text-left p-2 rounded-tl-lg">Statut</th>
                      <th className="text-right p-2">Nombre</th>
                      <th className="text-right p-2 rounded-tr-lg">Montant</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.devis.map((d, idx) => (
                      <tr key={idx} className="border-b border-gray-100">
                        <td className="p-2 capitalize">{d.statut}</td>
                        <td className="p-2 text-right">{d.count}</td>
                        <td className="p-2 text-right font-medium">{formatNumber(d.total)} XOF</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Dettes */}
            <div className="section bg-white rounded-xl shadow p-6">
              <h3 className="font-semibold text-indigo-600 border-b-2 border-indigo-100 pb-2 mb-4 text-base flex items-center gap-2"><TrendingDown className="w-4 h-4" /> Dettes fournisseurs</h3>
              <div className="space-y-3">
                <div className="flex justify-between p-3 bg-red-50 rounded-lg">
                  <span className="text-gray-600">Nouvelles dettes</span>
                  <span className="font-bold text-red-600">{formatNumber(data.dettes?.total_cree)} XOF</span>
                </div>
                <div className="flex justify-between p-3 bg-green-50 rounded-lg">
                  <span className="text-gray-600">Dettes remboursées</span>
                  <span className="font-bold text-green-600">{formatNumber(data.dettes?.total_paye)} XOF</span>
                </div>
                <div className="flex justify-between p-3 bg-gray-100 rounded-lg">
                  <span className="text-gray-600">Solde période</span>
                  <span className={`font-bold ${(data.dettes?.total_cree - data.dettes?.total_paye) > 0 ? 'text-red-600' : 'text-green-600'}`}>
                    {formatNumber(data.dettes?.total_cree - data.dettes?.total_paye)} XOF
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* ═══════════════ PRESTATIONS ═══════════════ */}
          {data.prestations?.length > 0 && (
            <div className="section bg-white rounded-xl shadow p-6 mb-6">
              <h3 className="font-semibold text-indigo-600 border-b-2 border-indigo-100 pb-2 mb-4 text-base flex items-center gap-2"><Wrench className="w-4 h-4" /> Prestations</h3>
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50">
                    <th className="text-left p-2">Statut</th>
                    <th className="text-right p-2">Nombre</th>
                    <th className="text-right p-2">Montant total</th>
                    <th className="text-right p-2">Montant payé</th>
                  </tr>
                </thead>
                <tbody>
                  {data.prestations.map((p, idx) => (
                    <tr key={idx} className="border-b">
                      <td className="p-2 capitalize">{p.statut?.replace('_', ' ')}</td>
                      <td className="p-2 text-right">{p.count}</td>
                      <td className="p-2 text-right font-medium">{formatNumber(p.total)} XOF</td>
                      <td className="p-2 text-right font-medium text-green-600">{formatNumber(p.total_paye)} XOF</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Footer */}
          <div className="footer text-center text-gray-400 text-sm py-4 border-t mt-6">
            <p>Bilan généré le {new Date().toLocaleDateString('fr-FR', { 
              day: 'numeric', 
              month: 'long', 
              year: 'numeric',
              hour: '2-digit',
              minute: '2-digit'
            })}</p>
            <p className="mt-1">{settings?.nom_entreprise || 'Mon Entreprise'} - Tous droits réservés</p>
          </div>
        </div>
      )}

      {/* Message si pas de données */}
      {!data && !loading && (
        <div className="bg-white rounded-xl shadow p-12 text-center">
          <div className="mb-4"><BarChart3 className="w-16 h-16 mx-auto text-indigo-400" /></div>
          <h3 className="text-xl font-semibold text-gray-800 mb-2">Générez votre bilan</h3>
          <p className="text-gray-500">
            Sélectionnez une période et cliquez sur "Générer le bilan" pour voir un rapport complet de votre activité.
          </p>
        </div>
      )}
    </div>
  );
};

export default BusinessReport;
